import { useEffect, useRef } from 'react'
import { isSyntheticPostIdea } from '../selectors/posts-page-items'
import { useIdeaStore, type LivePlatform, type PostIdea } from '../store/idea-store'

/**
 * Selection drives derivation — the single component-side entry point for
 * lazily deriving platform sibling creatives. Callers pass the full wanted
 * set (selected ∪ viewed/active); the hook requests each platform at most
 * once per mount, so a failed derivation is terminal for the surface
 * (reopening retries) instead of looping. previewSiblings itself dedupes
 * cached rows and concurrent in-flight requests across surfaces, and the
 * first attempt intentionally includes already-cached platforms so its
 * media-based filter can re-pull rows that were hydrated mid-generation.
 * See playbook/product/multi-platform/DESIGN.md (Phase 0).
 */
export function useDeriveSiblings(idea: PostIdea | undefined, wanted: LivePlatform[]): void {
  const previewSiblings = useIdeaStore(s => s.previewSiblings)
  const attemptedRef = useRef<{ projectId: string; platforms: Set<LivePlatform> } | null>(null)

  useEffect(() => {
    if (!idea || isSyntheticPostIdea(idea.projectId)) return
    if (attemptedRef.current?.projectId !== idea.projectId) {
      attemptedRef.current = { projectId: idea.projectId, platforms: new Set() }
    }
    const attempted = attemptedRef.current.platforms
    const candidateSet = new Set(idea.candidatePlatforms)
    const targets = wanted.filter(
      p => p !== idea.primaryPlatform && candidateSet.has(p) && !attempted.has(p)
    )
    if (targets.length === 0) return
    for (const p of targets) attempted.add(p)
    void previewSiblings(idea.projectId, targets)
  }, [idea, wanted, previewSiblings])
}
